// Subgroup helpers shared by the estimating grid, takeoff studio and proposal.
// A subgroup is a colored, optionally client-hidden band nested inside a scope group.
// Identity is (group, name). Stored on the bid as `scope_subgroups`; each scope line
// carries an optional `subgroup` name tying it to one.
export const DEFAULT_SUBGROUP_COLOR = "#2563EB";

export const findSub = (subs, group, name) =>
  (subs || []).find((s) => s.group === group && s.name === name) || null;

export const subColor = (subs, group, name) => {
  const s = findSub(subs, group, name);
  return (s && s.color) || DEFAULT_SUBGROUP_COLOR;
};

export const subShown = (subs, group, name) => {
  const s = findSub(subs, group, name);
  return s ? s.show_on_proposal !== false : true;
};

// hex (#rgb / #rrggbb) -> rgba string with alpha
export const tint = (hex, alpha = 0.1) => {
  if (!hex || typeof hex !== "string") return `rgba(37,99,235,${alpha})`;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return `rgba(37,99,235,${alpha})`;
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return `rgba(${r},${g},${b},${alpha})`;
};

// Split a group's items into ordered sections: ungrouped first (sub=null), then
// configured subgroups in config order, then any orphan subgroups by first appearance.
// Configured-but-empty subgroups are included so freshly-created ones render.
export const sectionize = (items, subs, group, getSub = (l) => l.subgroup) => {
  const byName = {};
  const appear = [];
  const ungrouped = [];
  (items || []).forEach((l) => {
    const sn = getSub(l) || null;
    if (!sn) { ungrouped.push(l); return; }
    if (!byName[sn]) { byName[sn] = []; appear.push(sn); }
    byName[sn].push(l);
  });
  const seen = new Set();
  const orderedNames = [];
  (subs || []).filter((s) => s.group === group).forEach((s) => {
    if (!seen.has(s.name)) { seen.add(s.name); orderedNames.push(s.name); }
  });
  appear.forEach((n) => { if (!seen.has(n)) { seen.add(n); orderedNames.push(n); } });
  const sections = [];
  if (ungrouped.length) sections.push({ sub: null, lines: ungrouped });
  orderedNames.forEach((n) => sections.push({
    sub: n, lines: byName[n] || [],
    color: subColor(subs, group, n), show: subShown(subs, group, n),
  }));
  return sections;
};
